import { useMemo } from 'react';
import { InfoMessage } from '@alf/uikit';
import { useConfig } from '@namespace/cms';
import {
  FORM_COMPONENTS_TYPES,
  FORM_VALIDATION_TYPES,
  FormBuilder
} from '@namespace/forms';
import { useI18n } from '@namespace/i18n';
import { useIbanCards } from '@namespace/payments-sw';
import { HTMLSnippet } from '@namespace/snippets';
import { PAYMENT_SYSTEMS } from '../../../../constants/paymentSystems';

import styles from '../index.module.css';

const IbanCard = ({ wallet, id, onWithdraw }) => {
  const { t, language } = useI18n();
  const { config } = useConfig(id);
  const { creditCardInfoSnippetId } = config;
  const { currency, minWithdraw, maxWithdraw, baseBalance } = wallet;
  const ibans = useIbanCards({
    currency
  });

  const onSubmit = async ({ password, amount, ibanList }) => {
    await onWithdraw({
      paySystem: 'corvus_pay',
      lang: language,
      iban: ibanList,
      paymentInstrumentId: PAYMENT_SYSTEMS.CASHDESK,
      password,
      amount
    });
  };

  const cardsOptions = useMemo(
    () =>
      ibans.reverse().map(({ account }) => ({
        name: account,
        value: account
      })),
    [ibans]
  );

  const defaultValues = {
    ibanList: '',
    amount: '',
    password: ''
  };
  const componentsList = [
    {
      type: FORM_COMPONENTS_TYPES.SELECT,
      name: 'ibanList',
      label: 'balance.withdraw.creditCard.ibanNumber',
      placeholder: 'balance.withdraw.creditCard.ibanNumberPlaceholder',
      validationTypes: [FORM_VALIDATION_TYPES.REQUIRED],
      classNames: {
        containerClassName: styles.fieldWrap
      },
      options: cardsOptions
    },
    {
      type: FORM_COMPONENTS_TYPES.AMOUNT_INPUT_FIELD,
      name: 'amount',
      currency,
      label: 'balance.withdraw.creditCard.amount',
      placeholder: 'balance.withdraw.creditCard.amountPlaceholder',
      min: 0,
      inputType: 'number',
      validationTypes: [
        FORM_VALIDATION_TYPES.REQUIRED,
        {
          type: FORM_VALIDATION_TYPES.MIN_VALIDATOR,
          parameters: [
            minWithdraw,
            'balance.withdraw.form.minAmount',
            [currency]
          ]
        },
        {
          type: FORM_VALIDATION_TYPES.MAX_VALIDATOR,
          parameters: [
            maxWithdraw ? Math.min(baseBalance, maxWithdraw) : baseBalance,
            'balance.withdraw.form.maxAmount',
            [currency]
          ]
        }
      ],
      hint: t('balance.withdraw.form.minAmount', [minWithdraw, currency]),
      classNames: {
        containerClassName: styles.fieldWrap,
        controlClassName: styles.controlWrap
      },
      isOnChangeValidation: true
    },
    {
      type: FORM_COMPONENTS_TYPES.PASSWORD_FIELD,
      name: 'password',
      classNames: {
        wrapperClassName: styles.fieldWrap
      },
      rulesList: [],
      autoComplete: 'password',
      label: 'balance.withdraw.creditCard.password',
      placeholder: 'balance.withdraw.creditCard.passwordPlaceholder',
      validationTypes: [FORM_VALIDATION_TYPES.REQUIRED]
    },
    {
      type: FORM_COMPONENTS_TYPES.SUBMIT_BUTTON,
      value: 'balance.withdraw.creditCard.submit',
      classNames: {
        className: styles.withdrawButton
      }
    }
  ];

  return (
    <div className={styles.container}>
      <InfoMessage icon="info" className={styles.info}>
        {t('balance.withdraw.creditCard.info')}
      </InfoMessage>
      <FormBuilder
        wrapperClassName={styles.form}
        components={componentsList}
        onSubmit={onSubmit}
        formConfig={{ mode: 'onBlur', defaultValues }}
        isNoValidate={true}
      />

      {creditCardInfoSnippetId && (
        <HTMLSnippet
          snippetId={creditCardInfoSnippetId}
          className={styles.infoSnippet}
        />
      )}
    </div>
  );
};

export default IbanCard;
